/**
 * Sign-in: a verified address and a password. One step.
 *
 * The frozen prototype shows a magic-link form ("giriş bağlantısı gönder"). The product keeps the
 * prototype's layout, card and copy structure and changes only the credential fields; the mismatch
 * is recorded in the visual-parity notes.
 *
 * WHAT USED TO BE HERE (PO 7.10)
 * ------------------------------
 * Two more stages: a six-digit TOTP challenge, and a first-time enrollment screen with a QR code
 * and a setup key. Both are gone, along with the state machine that routed between them. They are
 * not hidden behind a flag — there is no `stage` left, no `qrcode` import and no adapter method to
 * call — so no render, bookmark or future edit can arrive at them.
 *
 * That removes screens from this application; it removes nothing from anybody's account. A TOTP
 * factor somebody enrolled under the old rule is untouched at the provider.
 *
 * Nothing entered here is persisted by the application: the password is passed straight to the
 * provider and dropped.
 *
 * «PAROLAMI UNUTTUM»
 * ------------------
 * The same card, in a second view rather than a second route: the recovery request is one address
 * and one button, and sending somebody to a page of their own for that loses the address they had
 * already typed. Which is the other half of it — the address carries over, so nobody retypes it.
 *
 * The confirmation is the same sentence whether or not the address belongs to an account. That is
 * not vagueness for its own sake: this form can be submitted by anyone who can reach the login
 * page, so an answer that differed would turn it into a way of asking «does this person work
 * here». Supabase answers its own endpoint the same way for the same reason, and the adapter maps
 * every refusal that is not a rate limit onto one code, so there is no branch left in which
 * existence could show.
 */
import { type FormEvent, type ReactElement, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSession } from '../auth/session';
import { authMessage } from '../auth/supabaseAdapter';
import { Box, Button, TextField } from '../ui';
import { BrandMark } from '../ui/Icon';
import { Mosaic } from '../ui/Mosaic';

/**
 * «A password was just set», carried as a flag rather than as text.
 *
 * `ResetPasswordScreen` replaces its own history entry with this one when the provider has
 * accepted a new password. The sentence lives here, so nothing that a history entry carries is
 * ever rendered — the flag decides whether to show it, and this file decides what it says.
 */
function passwordJustSet(state: unknown): boolean {
  return (
    typeof state === 'object' &&
    state !== null &&
    (state as { passwordJustSet?: unknown }).passwordJustSet === true
  );
}

/**
 * The one sentence a recovery request ever answers with.
 *
 * Fixed text, held here rather than built from the outcome, so there is nothing for a future edit to
 * interpolate an address or a provider's opinion into.
 */
const RECOVERY_SENT =
  'Bu adres için parola yenileme mümkünse bağlantı e-posta ile gönderildi. Gelen kutunuzu kontrol edin.';

export function LoginScreen(): ReactElement {
  const { signIn, requestPasswordRecovery } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const justSetPassword = passwordJustSet(useLocation().state);
  /* Which half of the card is showing, and the address it is about. */
  const [recovering, setRecovering] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoverySent, setRecoverySent] = useState(false);

  async function submitPassword(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(email.trim(), password);
      setPassword('');
    } catch (cause) {
      setError(authMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  /** Opens the recovery view with whatever address was already typed, so nobody retypes it. */
  function openRecovery(): void {
    setRecoveryEmail(email.trim());
    setRecovering(true);
    setRecoverySent(false);
    setError(null);
  }

  /** Back to the sign-in half. The password field was never filled in over there. */
  function closeRecovery(): void {
    setRecovering(false);
    setRecoverySent(false);
    setError(null);
  }

  async function submitRecovery(event: FormEvent): Promise<void> {
    event.preventDefault();
    const address = recoveryEmail.trim();
    if (address.length === 0) return;
    setError(null);
    setBusy(true);
    try {
      await requestPasswordRecovery(address);
      /*
       * Nothing is read from the call: it answers `void`, and what is shown is the fixed sentence.
       * No session was established either — this screen is still the signed-out one.
       */
      setRecoverySent(true);
    } catch (cause) {
      setError(authMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-wrap">
      <Mosaic cols={48} rows={3} className="login-mosaic" />
      <div className="login-card">
        <span className="brand" style={{ padding: 0 }}>
          <BrandMark />
          <span className="txt">
            <b>HPW Credentials</b>
            <span className="sub">Happy Place to Work</span>
          </span>
        </span>

        <div>
          <h1>Yönetici girişi</h1>
          <p className="muted" style={{ marginTop: 6 }}>
            Doğrulanmış kurumsal e-posta adresiniz ve parolanızla giriş yapın. Alıcı hesabı yoktur; yalnız
            yöneticiler giriş yapar.
          </p>
        </div>
        {justSetPassword && !recovering ? (
          <Box tone="ok">Parolanız oluşturuldu. Yeni parolanızla giriş yapabilirsiniz.</Box>
        ) : null}
        {recovering ? (
          /*
           * The recovery half: one address, one button. No password field — there is no password to
           * give here, and a field that did nothing would invite one.
           */
          <form className="stack" style={{ gap: 14 }} onSubmit={(event) => void submitRecovery(event)}>
            <p className="muted" style={{ margin: 0 }}>
              Hesabınızın e-posta adresini yazın. Parolanızı yenilemeniz için bir bağlantı göndereceğiz.
            </p>
            <TextField
              label="E-posta"
              type="email"
              autoComplete="username"
              placeholder="ad@happyplacetowork.com.tr"
              value={recoveryEmail}
              onChange={(event) => setRecoveryEmail(event.target.value)}
              required
            />
            {recoverySent ? <Box tone="ok">{RECOVERY_SENT}</Box> : null}
            {error ? <Box tone="warn">{error}</Box> : null}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              block
              busy={busy}
              disabled={recoveryEmail.trim().length === 0}
            >
              Parola yenileme bağlantısı gönder
            </Button>
            {/* The reference's own quiet control, not a new link style. */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              block
              disabled={busy}
              data-act="back-to-login"
              onClick={closeRecovery}
            >
              Giriş ekranına dön
            </Button>
          </form>
        ) : (
          <form className="stack" style={{ gap: 14 }} onSubmit={(event) => void submitPassword(event)}>
            <TextField
              label="E-posta"
              type="email"
              autoComplete="username"
              placeholder="ad@happyplacetowork.com.tr"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
            <TextField
              label="Parola"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
            {error ? <Box tone="warn">{error}</Box> : null}
            <Button type="submit" variant="primary" size="lg" block busy={busy}>
              Giriş yap
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              block
              disabled={busy}
              data-act="forgot-password"
              onClick={openRecovery}
            >
              Parolamı unuttum
            </Button>
          </form>
        )}
      </div>
    </main>
  );
}
